<template>
<div class="card">
    <div class="card-header">Physician Metrics</div>
    
    <div v-if="physician.system_links.length == 0 || physician.system_links[0].system_id == null" class="card-body">
        <div class="row">
            <div class="col text-center text-gray-500">
                <i class="fal fa-user-alt-slash text-3xl"></i>
                <div class="text-2xl">Metrics Not available</div>
                <p>Physician is not linked to an EMR or other remote system.</p>
            </div>
        </div>
    </div>

    <div v-else class="card-body">

        <div v-if="!data_loaded" class="p-4" style="position:relative">
            <img class="center-me" src="/images/loading-color.svg">
        </div>

        <div v-else class="row">

            <div class="col-12 col-sm-6 mb-3">
                <div class="rounded bg-purple-600 h-100 p-3">
                    <label class="text-base text-purple-300">Referrer Score</label>
                    <div class="text-5xl text-white font-weight-bold">{{ physician.referrer_score }}</div>
                    <div class="d-flex mt-2 align-items-center leading-none">
                        <div class="progress progress-xs flex-grow-1 bg-purple-400">
                            <div class="progress-bar bg-white" :style="'width:' + physician.referrer_score + '%'"></div>
                        </div>
                        <div class="ml-1 text-purple-100 text-sm">100</div>
                    </div>
                </div>
            </div>

			<div class="col-12 col-sm-6 mb-3">
				<div class="rounded bg-purple-400 h-100 p-3">
					<label class="text-base text-purple-200">Referred Patients</label>
					<div class="d-flex align-items-baseline">
						<div class="text-4xl text-white font-weight-bold mr-2" v-html="physician.referred_patients ? physician.referred_patients : 0" />     
						<line-chart
							:chart-data="datacollection.referred_patients"
							:options="options_sparkline"
							:style="chartStyles"
						/>
					</div>
					<div class="d-flex mt-2 align-items-center leading-none">
						<div class="progress progress-xs flex-grow-1 bg-purple-600">
							<div class="progress-bar bg-white" :style="'width:' + referred_patients_rank + '%'"></div>
						</div>
						<div class="ml-1 text-purple-100 text-sm" v-html="physician.client_metrics.max_referred_patients" />
					</div>
				</div>
			</div>

			<div class="col-12 col-sm-6 mb-3">
				<div class="rounded bg-purple-400 h-100 p-3">
					<label class="text-base text-purple-200">Patient Visits</label>
					<div class="d-flex align-items-baseline">
						<div class="text-4xl text-white font-weight-bold mr-2" v-html="physician.patient_visits ? physician.patient_visits : 0" />     
						<line-chart
							:chart-data="datacollection.patient_visits"
							:options="options_sparkline"
							:style="chartStyles"
						/>
					</div>
					<div class="d-flex mt-2 align-items-center leading-none">
						<div class="progress progress-xs flex-grow-1 bg-purple-600">
							<div class="progress-bar bg-white" :style="'width:' + patient_visits_rank + '%'"></div>
						</div>
						<div class="ml-1 text-purple-100 text-sm" v-html="physician.client_metrics.max_patient_visits" />
					</div>
				</div>
			</div>

			<div class="col-12 col-sm-6 mb-3">
				<div class="rounded bg-purple-400 h-100 p-3">
					<label class="text-base text-purple-200">Total Charges</label>
					<div class="d-flex align-items-baseline">
						<div class="text-4xl text-white font-weight-bold mr-2"><sup class="text-purple-200">$</sup>{{ physician.generated_charges ? numeral(physician.generated_charges).format('0a') : 0 }}</div>     
						<line-chart
							:chart-data="datacollection.total_charges"
							:options="options_sparkline"
							:style="chartStyles"
						/>
					</div>
					<div class="d-flex mt-2 align-items-center leading-none">
						<div class="progress progress-xs flex-grow-1 bg-purple-600">
							<div class="progress-bar bg-white" :style="'width:' + total_charges_rank + '%'"></div>
						</div>
						<div class="ml-1 text-purple-100 text-sm" v-html="numeral(physician.client_metrics.max_generated_charges).format('$0a')" />
					</div>
				</div>
			</div>

            <div class="col-12 col-sm-6 mb-3 mb-sm-0">
            	<div class="rounded bg-blue-100 h-100 p-3">
            		<label class="text-base text-blue-600">Top Procedures (Charges)</label>
	                <horizontal-bar-chart
	                    :chart-data="datacollection.procedures_charges"
	                    :options="options_barchart_charges"
	                    :height="150"
	                />
	            </div>
            </div>
            
            <div class="col-12 col-sm-6">
            	<div class="rounded bg-blue-100 h-100 p-3">
            		<label class="text-base text-blue-600">Top Procedures (Volume)</label>
	                <horizontal-bar-chart
	                	:chart-data="datacollection.procedures_volume"
	                	:options="options_barchart"
	                	:height="150"
	                />
            	</div>
            </div>

        </div>
    </div>
</div>
</template>


<script>
  import LineChart from '../helpers/LineChart.js'
  import HorizontalBarChart from '../helpers/HorizontalBarChart.js'

  export default {
    components: {
        LineChart,
        HorizontalBarChart
    },

    props: ['physician'],

    data () {
        return {
            data_loaded: false,

            labels: {
                months: [],
                procedures_charges: [],
                procedures_volume: [],
            },

            referred_patients: [],
            patient_visits: [],
            total_charges: [],
            procedures_charges: [],
            procedures_volume: [],

            datacollection: {
                referred_patients: null,
                patient_visits: null,
                total_charges: null,
                procedures_charges: null,
                procedures_volume: null,
            },

            chartStyles: {
                height: '30px',
            },

            options_sparkline: {
                responsive: true,                
                maintainAspectRatio: false,
                title: {
                    display: false,
                },                
                legend: {
                    display: false
                },                 
                animation: {
                    duration: 0, // general animation time
                },                
                scales: {
                    xAxes: [{
                        display: false,
                    }],
                    yAxes: [{
                        display: false, 
                        ticks: {
                            min: 0
                        }                        
                    }],
                },                
            },

            options_barchart_charges: {
                height: '100%',
                responsive: true,                
                maintainAspectRatio: false,
                title: {
                    display: false,
                },                
                legend: {
                    display: false
                },                 
                animation: {
                    duration: 0, // general animation time
                },                
                scales: {
                    xAxes: [{
                    	gridLines: {
                    		drawTicks: false,
                    		borderDash: [3, 1],
                    	},
                        ticks: {
                        	callback: function (value, index, values) {
                        		return numeral(value).format('$0a')
                        	},
                            min: 0,
                            display: true,
                            padding:2,
                        }
                    }],
                    yAxes: [{
                        gridLines: {
                            display:false
                        }  
                    }],
                }                
            }, 

            options_barchart: {
                height: '100%',
                responsive: true,                
                maintainAspectRatio: false,
                title: {
                    display: false,
                },                
                legend: {
                    display: false
                },                 
                animation: {
                    duration: 0, // general animation time
                },                
                scales: {
                    xAxes: [{
                    	gridLines: {
                    		drawTicks: false,
                    		borderDash: [3, 1],
                    	},                    	
                        ticks: {
                            min: 0,
                            display: true
                        }
                    }],
                    yAxes: [{
                        gridLines: {
                            display:false
                        }  
                    }],
                }                
            },         
        }
    },

    mounted () {
        //this.fillData();
        if (this.physician.system_links.length > 0 && this.physician.system_links[0].system_id != null) {
            this.getStats();
        }
    },

    methods: {
        getStats() {
            axios.get('/api/physicians/' + this.physician.id + '/stats')
                .then(({data}) => {
                    this.data_loaded = true;

                    data.monthly.forEach(monthData => {
                        this.labels.months.push(monthData.month);                        
                        this.referred_patients.push(monthData.referred_patients);
                        this.patient_visits.push(monthData.patient_visits);
                        this.total_charges.push(monthData.total_charges);
                    });

                    data.procs.charges.forEach(charges => {
                        this.labels.procedures_charges.push(charges.FacilityCPT4Code);
                        this.procedures_charges.push(charges.total_charges);
                    });

                    data.procs.volume.forEach(volume => {
                        this.labels.procedures_volume.push(volume.FacilityCPT4Code);
                        this.procedures_volume.push(volume.total_quantity);
                    });

                    this.fillData();
                })
        },


        fillData() {
            const borderColor = '#f4e1f5';
            const backgroundColor = '#cba1cd';
            const backgroundColorBar = '#3385cb';

            this.datacollection.referred_patients = {
                labels: this.labels.months,
                datasets: [{
                    label: 'Referred Patients',
                    data: this.referred_patients,
                    borderWidth: 2,
                    pointRadius: 0,
                    borderColor: borderColor,
                    backgroundColor: backgroundColor,        
                }]    
            };

            this.datacollection.patient_visits = {
                labels: this.labels.months,
                datasets: [{
                    label: 'Patient Visits',
                    data: this.patient_visits,
                    borderWidth: 2,
                    pointRadius: 0,
                    borderColor: borderColor,
                    backgroundColor: backgroundColor,        
                }]            
            };

            this.datacollection.total_charges = {
                labels: this.labels.months,
                datasets: [{
                    label: 'Total Charges',
                    data: this.total_charges,
                    borderWidth: 2,
                    pointRadius: 0,
                    borderColor: borderColor,
                    backgroundColor: backgroundColor,    
                }]          
            };

            this.datacollection.procedures_charges = {
                type: 'horizontalBar',
                labels: this.labels.procedures_charges,
                datasets: [{
                    label: 'Top Procedures (charges)',
                    data: this.procedures_charges,
                    barPercentage: 0.66,
                    borderWidth: 0,
                    backgroundColor: backgroundColorBar,
                }]
            };

            this.datacollection.procedures_volume = {
                type: 'horizontalBar',
                labels: this.labels.procedures_volume,
                datasets: [{
                    label: 'Top Procedures (volume)',
                    data: this.procedures_volume,
                    barPercentage: 0.66,
                    borderWidth: 0,
                    backgroundColor: backgroundColorBar,
                }]                
            };

        },
    },

    computed: {
        referred_patients_rank() {
            if ( this.physician.client_metrics.max_referred_patients) {
                return (this.physician.referred_patients / this.physician.client_metrics.max_referred_patients) * 100
            }
            return 0;
        },

        patient_visits_rank() {
            if ( this.physician.client_metrics.max_patient_visits) {
                return (this.physician.patient_visits / this.physician.client_metrics.max_patient_visits) * 100
            }
            return 0;
        },

        total_charges_rank() {
            if ( this.physician.client_metrics.max_generated_charges) {
                return (this.physician.generated_charges / this.physician.client_metrics.max_generated_charges) * 100
            }
            return 0;
        },
        
    },
}
</script>

<style>
.test { width:100%; height:100%; position: relative; }

canvas {
width: 100% !important;
}
</style>